import { Position, type Edge, type Node } from "@xyflow/react";

import type { BrokerAccountRef } from "../../../utils/brokerAccountRef";
import {
  brokerAccountRefKey,
  resolveEntityBrokerAccounts,
} from "../../../utils/brokerAccountRef";
import type { WorkspaceEdge, WorkspaceNode, WorkspaceNodePosition, WorkspaceNodeType } from "../../../types/workspace";
import {
  WORKSPACE_ACCOUNT_CARD_SIZE,
  WORKSPACE_GRAPH_ROW_GAP,
  WORKSPACE_NODE_CARD_HEIGHT,
  WORKSPACE_NODE_CARD_WIDTH,
  WORKSPACE_NODE_COLLISION_GAP,
  WORKSPACE_NODE_SNAP_GRID,
  WORKSPACE_PLACEHOLDER_COLUMN_GAP,
  WORKSPACE_PLACEHOLDER_NODE_SIZE,
  getWorkspaceNodeDimensions,
  snapWorkspaceNodeByCenter,
} from "./workspaceGraphConstants";
import {
  listExecutingAccountBridges,
  type ExecutingAccountBridge,
} from "./layoutStrategyGraphSequential";
import { resolveWorkspaceNodePosition } from "./workspaceLayoutUtils";
import { workspaceNodeShowsForwardPlaceholder } from "./workspacePlaceholderPolicy";

/** Ephemeral React Flow node type for policy executing-account diamonds. */
export const WORKSPACE_EXECUTING_ACCOUNT_NODE_TYPE = "workspaceExecutingAccount";

/** Diamond face — same outer size as persisted account diamonds. */
export const WORKSPACE_EXECUTING_ACCOUNT_DIAMOND_SIZE = WORKSPACE_ACCOUNT_CARD_SIZE;

/**
 * How far the diamond sits past the card’s left edge (half in / half out).
 * Equal to half the diamond bounding box.
 */
export const WORKSPACE_EXECUTING_ACCOUNT_DIAMOND_OVERHANG = Math.round(
  WORKSPACE_EXECUTING_ACCOUNT_DIAMOND_SIZE / 2
);

/**
 * @deprecated No stem gap; diamond overhangs the card instead.
 * Prefer {@link WORKSPACE_EXECUTING_ACCOUNT_DIAMOND_OVERHANG}.
 */
export const WORKSPACE_EXECUTING_ACCOUNT_METRICS_GAP = 0;

/**
 * @deprecated Metrics live inside the standard node card width.
 */
export const WORKSPACE_EXECUTING_ACCOUNT_METRICS_WIDTH = WORKSPACE_NODE_CARD_WIDTH;

/** Full Flow footprint: diamond overhang + standard pipeline card. */
export const WORKSPACE_EXECUTING_ACCOUNT_NODE_WIDTH =
  WORKSPACE_EXECUTING_ACCOUNT_DIAMOND_OVERHANG + WORKSPACE_NODE_CARD_WIDTH;

/** Match other pipeline entity cards. */
export const WORKSPACE_EXECUTING_ACCOUNT_NODE_HEIGHT = WORKSPACE_NODE_CARD_HEIGHT;

/** NodeToolbar placement should anchor to the card body, not the diamond overhang. */
export const WORKSPACE_EXECUTING_ACCOUNT_TOOLBAR_ANCHOR_INSETS = {
  left: WORKSPACE_EXECUTING_ACCOUNT_DIAMOND_OVERHANG,
} as const;

/**
 * React Flow NodeToolbar still measures the full node bounds; nudge the menu so
 * it lines up with the card edge like other pipeline nodes.
 */
export function workspaceExecutingAccountToolbarScreenOffset(
  position: (typeof Position)[keyof typeof Position],
  align: "start" | "center" | "end",
  zoom: number
): { x: number; y: number } {
  const overhang = WORKSPACE_EXECUTING_ACCOUNT_DIAMOND_OVERHANG * zoom;
  const halfOverhang = (WORKSPACE_EXECUTING_ACCOUNT_DIAMOND_OVERHANG / 2) * zoom;

  if (position === Position.Left) {
    return { x: overhang, y: 0 };
  }
  if (position === Position.Top || position === Position.Bottom) {
    if (align === "start") return { x: overhang, y: 0 };
    if (align === "center") return { x: halfOverhang, y: 0 };
  }
  return { x: 0, y: 0 };
}

/**
 * @deprecated Prefer {@link WORKSPACE_EXECUTING_ACCOUNT_NODE_HEIGHT} for stacking
 * and {@link WORKSPACE_EXECUTING_ACCOUNT_NODE_WIDTH} for collision / Flow size.
 */
export const WORKSPACE_EXECUTING_ACCOUNT_NODE_SIZE = WORKSPACE_EXECUTING_ACCOUNT_NODE_HEIGHT;

export type WorkspaceExecutingAccountNodeData = {
  parentNode: WorkspaceNode;
  account: BrokerAccountRef;
  label: string;
  /** Highlight when this exec diamond (or its parent policy) is selected. */
  isSelected?: boolean;
  isParentSelected?: boolean;
  focusFilterDimmed?: boolean;
  showActionsMenu?: boolean;
  mutating?: boolean;
  /** Switch active account and open `/workspace` hub (not a specific canvas). */
  onOpenAccountWorkspace?: (account: BrokerAccountRef) => void;
  /** Open executing-account detail sheet. */
  onOpenDetail?: () => void;
  /** Unassign this account from the parent policy. */
  onRemove?: () => void;
  onCloseActionsMenu?: () => void;
};

export function isWorkspaceExecutingAccountNodeId(nodeId: string): boolean {
  return nodeId.startsWith("exec-account:");
}

export function executingAccountNodeId(
  policyNodeId: string,
  account: BrokerAccountRef
): string {
  const broker = account.brokerName.trim().toLowerCase();
  const code = account.brokerAccountCode.trim();
  return `exec-account:${encodeURIComponent(policyNodeId)}:${encodeURIComponent(broker)}:${encodeURIComponent(code)}`;
}

function executingAccountEdgeId(policyNodeId: string, account: BrokerAccountRef): string {
  return `exec-account-edge:${encodeURIComponent(policyNodeId)}:${encodeURIComponent(account.brokerName.trim().toLowerCase())}:${encodeURIComponent(account.brokerAccountCode.trim())}`;
}

export function listPolicyExecutingAccounts(node: WorkspaceNode): BrokerAccountRef[] {
  if (node.type !== "policy" && node.type !== "combined_policy") return [];
  return resolveEntityBrokerAccounts({ accounts: node.meta?.accounts });
}

/** Forward pipeline children that occupy columns to the right of a policy/combined_policy. */
const FORWARD_PIPELINE_CHILD_TYPES: ReadonlySet<WorkspaceNodeType> = new Set<WorkspaceNodeType>([
  "combined_policy",
  "combined_simulation",
]);

const COMBINED_SIMULATION_TYPES: ReadonlySet<WorkspaceNodeType> = new Set<WorkspaceNodeType>([
  "combined_simulation",
]);

const COMBINED_POLICY_TYPES: ReadonlySet<WorkspaceNodeType> = new Set<WorkspaceNodeType>([
  "combined_policy",
]);

function childNodesOfTypes(
  parentId: string,
  edges: WorkspaceEdge[],
  allNodes: WorkspaceNode[],
  types: ReadonlySet<WorkspaceNodeType>
): WorkspaceNode[] {
  const byId = new Map(allNodes.map((node) => [node.id, node]));
  return edges
    .filter((edge) => edge.sourceNodeId === parentId)
    .map((edge) => byId.get(edge.targetNodeId))
    .filter((node): node is WorkspaceNode => Boolean(node && types.has(node.type)));
}

/**
 * Right edge (x + width) of the parent's card and any combined_policy /
 * combined_simulation descendants that sit in columns to its right.
 */
export function rightEdgeOfPolicyForwardPipeline(
  parent: WorkspaceNode,
  edges: WorkspaceEdge[],
  allNodes: WorkspaceNode[]
): number {
  const parentPosition = resolveWorkspaceNodePosition(parent);
  const { width: parentWidth } = getWorkspaceNodeDimensions(parent);
  let right = parentPosition.x + parentWidth;

  const direct = childNodesOfTypes(parent.id, edges, allNodes, FORWARD_PIPELINE_CHILD_TYPES);
  for (const child of direct) {
    const pos = resolveWorkspaceNodePosition(child);
    const { width } = getWorkspaceNodeDimensions(child);
    right = Math.max(right, pos.x + width);
    // policy → combined_policy → combined_simulation
    if (child.type === "combined_policy") {
      for (const grand of childNodesOfTypes(
        child.id,
        edges,
        allNodes,
        COMBINED_SIMULATION_TYPES
      )) {
        const gPos = resolveWorkspaceNodePosition(grand);
        const { width: gWidth } = getWorkspaceNodeDimensions(grand);
        right = Math.max(right, gPos.x + gWidth);
      }
    }
  }

  return right;
}

/**
 * Shared right edge for the executing-account column: past every policy /
 * combined_policy forward pipeline on the canvas. Terminal policies (no CP/CS
 * children) still park alongside EAs that hang off longer combined stacks.
 */
export function rightEdgeOfAllForwardPipelines(
  edges: WorkspaceEdge[],
  allNodes: WorkspaceNode[]
): number {
  let right = Number.NEGATIVE_INFINITY;
  for (const node of allNodes) {
    if (node.type !== "policy" && node.type !== "combined_policy") continue;
    right = Math.max(right, rightEdgeOfPolicyForwardPipeline(node, edges, allNodes));
  }
  if (Number.isFinite(right)) return right;

  for (const node of allNodes) {
    const pos = resolveWorkspaceNodePosition(node);
    const { width } = getWorkspaceNodeDimensions(node);
    right = Math.max(right, pos.x + width);
  }
  return Number.isFinite(right) ? right : 0;
}

/**
 * Vertical bounds of combined_policy / combined_simulation cards hanging off this
 * parent. Used to park executing-account diamonds clear of that stack so the
 * policy→exec edge does not run behind a combined card.
 */
export function verticalExtentOfPolicyForwardPipeline(
  parent: WorkspaceNode,
  edges: WorkspaceEdge[],
  allNodes: WorkspaceNode[]
): { top: number; bottom: number } | null {
  const intervals = forwardPipelineCardIntervals(parent, edges, allNodes);
  if (intervals.length === 0) return null;
  return {
    top: Math.min(...intervals.map((interval) => interval.top)),
    bottom: Math.max(...intervals.map((interval) => interval.bottom)),
  };
}

type VerticalInterval = { top: number; bottom: number };

function forwardPipelineCardIntervals(
  parent: WorkspaceNode,
  edges: WorkspaceEdge[],
  allNodes: WorkspaceNode[]
): VerticalInterval[] {
  const intervals: VerticalInterval[] = [];

  const visit = (node: WorkspaceNode) => {
    const pos = resolveWorkspaceNodePosition(node);
    const { height } = getWorkspaceNodeDimensions(node);
    intervals.push({ top: pos.y, bottom: pos.y + height });
  };

  for (const child of childNodesOfTypes(
    parent.id,
    edges,
    allNodes,
    FORWARD_PIPELINE_CHILD_TYPES
  )) {
    visit(child);
    if (child.type === "combined_policy") {
      for (const grand of childNodesOfTypes(
        child.id,
        edges,
        allNodes,
        COMBINED_SIMULATION_TYPES
      )) {
        visit(grand);
      }
    }
  }

  return intervals.sort((a, b) => a.top - b.top);
}

/**
 * Y for the long horizontal of a policy/combined → executing-account edge.
 * When combined cards sit in the corridor, always route underneath the block so
 * the link does not share the policy→combined sibling trunk (which looks like a
 * vertical bus with a late branch to the diamond).
 */
export function resolveExecutingAccountEdgeDetourY(
  parent: WorkspaceNode,
  edges: WorkspaceEdge[],
  allNodes: WorkspaceNode[],
  clearGap: number = WORKSPACE_NODE_COLLISION_GAP
): number | null {
  const extent = verticalExtentOfPolicyForwardPipeline(parent, edges, allNodes);
  if (!extent) return null;

  // Prefer underneath — the readable “go under the combined stack” path.
  return extent.bottom + clearGap;
}

/**
 * Shared column immediately right of every policy forward pipeline on the canvas
 * (policy → combined_policy → combined_simulation). Y stays on the parent spine;
 * edges detour around combined cards via `execDetourY`.
 */
export function computeExecutingAccountNodePosition(
  parent: WorkspaceNode,
  accountIndex: number,
  accountCount: number,
  edges: WorkspaceEdge[],
  allNodes: WorkspaceNode[],
  options?: { viewMode?: "account" | "strategy"; nodeSize?: number; maxSimulationsPerReport?: number }
): WorkspaceNodePosition {
  const nodeHeight = options?.nodeSize ?? WORKSPACE_EXECUTING_ACCOUNT_NODE_HEIGHT;
  const parentPosition = resolveWorkspaceNodePosition(parent);
  const { width: parentWidth, height: parentHeight } = getWorkspaceNodeDimensions(parent);
  const localPipelineRight = rightEdgeOfPolicyForwardPipeline(parent, edges, allNodes);
  const pipelineRight = Math.max(
    localPipelineRight,
    rightEdgeOfAllForwardPipelines(edges, allNodes)
  );

  let columnX = pipelineRight + WORKSPACE_PLACEHOLDER_COLUMN_GAP;

  // Forward "+" bumps only matter when this park is still local to the parent
  // (no sibling combined stack pushing the shared column further right).
  const parkIsLocal = pipelineRight <= localPipelineRight + 1;
  if (parkIsLocal) {
    const hasForwardPlaceholder = workspaceNodeShowsForwardPlaceholder(
      parent,
      edges,
      allNodes,
      { viewMode: options?.viewMode, maxSimulationsPerReport: options?.maxSimulationsPerReport }
    );
    if (hasForwardPlaceholder && localPipelineRight <= parentPosition.x + parentWidth + 1) {
      // Placeholder is immediately right of parent (no combined children yet).
      columnX += WORKSPACE_PLACEHOLDER_NODE_SIZE + WORKSPACE_PLACEHOLDER_COLUMN_GAP;
    } else {
      // Placeholder on a combined_policy child that still needs a run-sim diamond.
      const combinedChildren = childNodesOfTypes(
        parent.id,
        edges,
        allNodes,
        COMBINED_POLICY_TYPES
      );
      const placeholderBlocks = combinedChildren.some((child) =>
        workspaceNodeShowsForwardPlaceholder(child, edges, allNodes, {
          viewMode: options?.viewMode,
          maxSimulationsPerReport: options?.maxSimulationsPerReport,
        })
      );
      if (placeholderBlocks) {
        columnX += WORKSPACE_PLACEHOLDER_NODE_SIZE + WORKSPACE_PLACEHOLDER_COLUMN_GAP;
      }
    }
  }

  const stackHeight =
    accountCount * nodeHeight + Math.max(0, accountCount - 1) * WORKSPACE_GRAPH_ROW_GAP;
  const parentCenterY = parentPosition.y + parentHeight / 2;
  const stackTop = parentCenterY - stackHeight / 2;
  const y = stackTop + accountIndex * (nodeHeight + WORKSPACE_GRAPH_ROW_GAP);

  // Snap by diamond size so the logo stays on the pipeline spine; the metrics
  // card extends to the right outside that snapped box.
  const snapped = snapWorkspaceNodeByCenter(
    { x: columnX, y },
    {
      width: WORKSPACE_EXECUTING_ACCOUNT_DIAMOND_SIZE,
      height: WORKSPACE_EXECUTING_ACCOUNT_DIAMOND_SIZE,
    },
    WORKSPACE_NODE_SNAP_GRID
  );
  return { x: snapped.x, y };
}

function executingAccountBridgeKey(policyNodeId: string, account: BrokerAccountRef): string {
  return `${policyNodeId}::${brokerAccountRefKey(account)}`;
}

function bridgeTargetByPolicyAccount(
  bridges: ExecutingAccountBridge[]
): Map<string, ExecutingAccountBridge> {
  const map = new Map<string, ExecutingAccountBridge>();
  for (const bridge of bridges) {
    map.set(executingAccountBridgeKey(bridge.fromPolicyNodeId, bridge.account), bridge);
  }
  return map;
}

export function toExecutingAccountFlowNodes(
  nodes: WorkspaceNode[],
  edges: WorkspaceEdge[],
  options?: {
    viewMode?: "account" | "strategy";
    selectedNodeId?: string | null;
    actionsMenuNodeId?: string | null;
    mutating?: boolean;
    focusMatchedNodeIds?: Set<string> | null;
    onOpenAccountWorkspace?: (account: BrokerAccountRef) => void;
    onCloseActionsMenu?: () => void;
    /**
     * Strategy Sequential: when the fed account card is already on the canvas,
     * skip the ephemeral diamond (policy links straight to that account).
     */
    omitDiamondsForBridgeTargets?: boolean;
    maxSimulationsPerReport?: number;
  }
): Array<Node<WorkspaceExecutingAccountNodeData>> {
  const bridges = options?.omitDiamondsForBridgeTargets
    ? listExecutingAccountBridges(nodes, edges)
    : [];
  const bridged = bridgeTargetByPolicyAccount(bridges);

  return nodes.flatMap((parent) => {
    const accounts = listPolicyExecutingAccounts(parent);
    if (accounts.length === 0) return [];

    return accounts.flatMap((account, index) => {
      if (
        options?.omitDiamondsForBridgeTargets &&
        bridged.has(executingAccountBridgeKey(parent.id, account))
      ) {
        return [];
      }
      const id = executingAccountNodeId(parent.id, account);
      const isSelected =
        options?.selectedNodeId === id || options?.actionsMenuNodeId === id;
      return [
        {
          id,
          type: WORKSPACE_EXECUTING_ACCOUNT_NODE_TYPE,
          position: computeExecutingAccountNodePosition(
            parent,
            index,
            accounts.length,
            edges,
            nodes,
            { viewMode: options?.viewMode, maxSimulationsPerReport: options?.maxSimulationsPerReport }
          ),
          width: WORKSPACE_EXECUTING_ACCOUNT_NODE_WIDTH,
          height: WORKSPACE_EXECUTING_ACCOUNT_NODE_HEIGHT,
          draggable: false,
          selectable: true,
          connectable: false,
          focusable: false,
          selected: isSelected,
          data: {
            parentNode: parent,
            account,
            label: account.brokerAccountCode,
            isSelected,
            isParentSelected: options?.selectedNodeId === parent.id,
            showActionsMenu: options?.actionsMenuNodeId === id,
            mutating: options?.mutating,
            onOpenAccountWorkspace: options?.onOpenAccountWorkspace,
            onCloseActionsMenu: options?.onCloseActionsMenu,
            focusFilterDimmed:
              options?.focusMatchedNodeIds != null &&
              !options.focusMatchedNodeIds.has(parent.id),
          },
        },
      ];
    });
  });
}

export function toExecutingAccountFlowEdges(
  nodes: WorkspaceNode[],
  edges: WorkspaceEdge[],
  options?: {
    /**
     * Strategy Sequential: draw policy → fed account when the target is on-canvas
     * (no intermediate diamond).
     */
    linkDirectlyToBridgeTargets?: boolean;
  }
): Edge[] {
  const nodeIds = new Set(nodes.map((node) => node.id));
  const bridges = options?.linkDirectlyToBridgeTargets
    ? listExecutingAccountBridges(nodes, edges)
    : [];
  const bridged = bridgeTargetByPolicyAccount(bridges);

  return nodes.flatMap((parent) => {
    const accounts = listPolicyExecutingAccounts(parent);
    if (accounts.length === 0) return [];
    const execDetourY = resolveExecutingAccountEdgeDetourY(parent, edges, nodes);

    return accounts.map((account) => {
      const bridge = bridged.get(executingAccountBridgeKey(parent.id, account));
      if (
        options?.linkDirectlyToBridgeTargets &&
        bridge &&
        nodeIds.has(bridge.toAccountNodeId)
      ) {
        return {
          id: `exec-account-direct:${encodeURIComponent(parent.id)}:${encodeURIComponent(account.brokerName)}:${encodeURIComponent(account.brokerAccountCode)}:${encodeURIComponent(bridge.toAccountNodeId)}`,
          source: parent.id,
          target: bridge.toAccountNodeId,
          sourceHandle: "source",
          targetHandle: "target",
          type: "workspacePipeline",
          selectable: false,
          focusable: false,
          data: execDetourY != null ? { execDetourY } : undefined,
        };
      }
      return {
        id: executingAccountEdgeId(parent.id, account),
        source: parent.id,
        target: executingAccountNodeId(parent.id, account),
        sourceHandle: "source",
        targetHandle: "target",
        type: "workspacePipeline",
        selectable: false,
        focusable: false,
        data: execDetourY != null ? { execDetourY } : undefined,
      };
    });
  });
}

/**
 * Soft edges from exec diamonds → matching fed-account nodes (Sequential story).
 * Visual only; not persisted. Unused when {@link toExecutingAccountFlowEdges}
 * already links policy → account directly.
 */
export function toExecutingAccountContinuationFlowEdges(
  nodes: WorkspaceNode[],
  bridges: Array<{
    fromPolicyNodeId: string;
    toAccountNodeId: string;
    account: { brokerName: string; brokerAccountCode: string };
  }>
): Edge[] {
  const nodeIds = new Set(nodes.map((node) => node.id));
  return bridges.flatMap((bridge) => {
    if (!nodeIds.has(bridge.toAccountNodeId)) return [];
    const execId = executingAccountNodeId(bridge.fromPolicyNodeId, bridge.account);
    return [
      {
        id: `exec-account-cont:${encodeURIComponent(bridge.fromPolicyNodeId)}:${encodeURIComponent(bridge.account.brokerName)}:${encodeURIComponent(bridge.account.brokerAccountCode)}:${encodeURIComponent(bridge.toAccountNodeId)}`,
        source: execId,
        target: bridge.toAccountNodeId,
        sourceHandle: "source",
        targetHandle: "target",
        type: "workspacePipeline",
        selectable: false,
        focusable: false,
      },
    ];
  });
}

/** Parse parent policy node id from an `exec-account:…` flow id. */
export function parentPolicyNodeIdFromExecutingAccountNodeId(nodeId: string): string | null {
  if (!isWorkspaceExecutingAccountNodeId(nodeId)) return null;
  const parts = nodeId.slice("exec-account:".length).split(":");
  if (parts.length < 3) return null;
  try {
    return decodeURIComponent(parts[0]!);
  } catch {
    return null;
  }
}
